import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Link2, MessageSquare, Wrench } from "lucide-react";
import { api, NATURE, TOOL_ROUTES } from "@/lib/api";
import { MaturityBadge, NatureBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { t } from "@/lib/i18n";

const Section = ({ title, children }) => (
  <section className="mt-5"><div className="eyebrow mb-2">{title}</div>{children}</section>
);

export const NodeDetail = ({ id, onSelect }) => {
  const [node, setNode] = useState(null);
  useEffect(() => { setNode(null); api.get(`/nodes/${id}`).then((r) => setNode(r.data)).catch(() => setNode(false)); }, [id]);
  if (node === null) return <div className="surface p-6 text-sm text-muted-foreground">{t("Caricamento nodo…")}</div>;
  if (node === false) return <div className="surface p-6 text-sm text-destructive">{t("Nodo non trovato.")}</div>;

  return (
    <article data-testid="node-detail" className="surface overflow-hidden fade-up">
      <div className="h-1.5" style={{ background: node.color }} />
      <div className="p-5 lg:p-6">
        <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          {node.breadcrumb.map((b) => (<span key={b.id} className="flex items-center gap-1"><button onClick={() => onSelect(b.id)} className="hover:text-foreground">{b.title}</button><span>/</span></span>))}
          <span className="text-foreground">{node.title}</span>
        </div>
        <div className="flex items-start justify-between gap-3 mt-2">
          <h2 data-testid="node-detail-title" className="text-xl lg:text-2xl font-bold tracking-tight">{node.code && <span className="num text-muted-foreground mr-2 text-base">{node.code}</span>}{node.title}</h2>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap"><MaturityBadge maturity={node.maturity} /><NatureBadge nature={node.nature} /><span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded-full px-2 py-0.5">{t("Livello")} {node.level}</span></div>
        <p data-testid="node-detail-summary" className="text-sm leading-relaxed mt-4 text-foreground/90">{node.summary}</p>

        <div className="flex flex-wrap gap-2 mt-4">
          {node.tool && <Button asChild size="sm" data-testid="node-open-tool-btn"><Link to={TOOL_ROUTES[node.tool]}><Wrench size={14} className="mr-1.5" /> {t("Apri strumento")}</Link></Button>}
          <Button asChild size="sm" variant="outline" data-testid="node-ask-role-btn"><Link to={`/team?role=${node.branch_id}`}><MessageSquare size={14} className="mr-1.5" /> {t("Chiedi al ruolo")}</Link></Button>
        </div>

        {node.kpis.length > 0 && (
          <Section title={t("Formule e KPI")}>
            <ul className="space-y-1">{node.kpis.map((k) => <li key={k} className="num text-xs bg-secondary rounded px-3 py-2 border border-border">{k}</li>)}</ul>
          </Section>
        )}

        {node.concepts.length > 0 && (
          <Section title={t("Concetti chiave")}>
            <div className="flex flex-wrap gap-1.5">{node.children.map((c) => (
              <button key={c.id} data-testid={`concept-chip-${c.id}`} onClick={() => onSelect(c.id)} className="text-xs px-2.5 py-1 rounded-full border border-border hover:border-primary hover:text-primary transition-colors">{c.title}</button>
            ))}</div>
          </Section>
        )}

        {node.level === 1 && node.children.length > 0 && (
          <Section title={t("Sotto-rami")}>
            <div className="grid sm:grid-cols-2 gap-2">{node.children.map((c) => (
              <button key={c.id} data-testid={`child-node-${c.id}`} onClick={() => onSelect(c.id)} className="text-left surface-elevated p-3 hover:border-primary/60 transition-colors">
                <div className="text-sm font-medium">{c.title}</div>
                <div className="flex gap-1.5 mt-1.5"><MaturityBadge maturity={c.maturity} /><NatureBadge nature={c.nature} /></div>
              </button>
            ))}</div>
          </Section>
        )}

        <Section title={`${t("File fonte")} (${node.file_docs.length})`}>
          {node.file_docs.length === 0 && <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-3">{t("Nessun file collegato: nodo da consolidare con materiali ancora da assimilare.")}</div>}
          <ul className="space-y-1.5">{node.file_docs.map((f) => (
            <li key={f.id} data-testid={`node-file-${f.id}`} className="flex items-start gap-2 text-sm surface-elevated p-3">
              <FileText size={15} className="mt-0.5 shrink-0" style={{ color: NATURE[f.nature]?.text }} />
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{f.name}</div>
                <div className="text-xs text-muted-foreground">{f.author} · {f.kind} · {t(f.status === "assimilato" ? "assimilato" : "da assimilare")}</div>
              </div>
              <NatureBadge nature={f.nature} />
            </li>
          ))}</ul>
        </Section>

        {(node.related.length > 0 || node.incoming.length > 0) && (
          <Section title={t("Collegamenti trasversali")}>
            <div className="flex flex-wrap gap-1.5">
              {node.related.map((r) => (
                <button key={r.id} data-testid={`related-node-${r.id}`} onClick={() => onSelect(r.id)} className="text-xs px-2.5 py-1 rounded-full border flex items-center gap-1 hover:opacity-80" style={{ borderColor: r.color, color: r.color }}><Link2 size={11} /> {r.title}</button>
              ))}
              {node.incoming.map((r) => (
                <button key={`in-${r.id}`} onClick={() => onSelect(r.id)} className="text-xs px-2.5 py-1 rounded-full border border-border text-muted-foreground flex items-center gap-1 hover:text-foreground"><ArrowUpRight size={11} /> {r.title}</button>
              ))}
            </div>
          </Section>
        )}
      </div>
    </article>
  );
};
